/**
 * Solanaland
 *
 * Author: Venus Lumanglas
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useAsyncDebounce } from "react-table";
import moment from "moment";
import { api } from "core/api";
import { Card, Input, SvgIcon, Button, Select, ControlledTable } from "core/components";
import {
  admin_purchase_request_path,
  approve_admin_purchase_request_path,
  cancel_admin_purchase_request_path,
  replenishment_requests_admin_material_reordering_index_path
} from "routes";
import { PurchaseRequestStatus } from "core/schema";
import { useForm } from "react-hook-form";
import ReplenishmentRequest from "./ReplenishmentRequest";
import ControlledTableContext from "core/components/ControlledTableContext";

const ReplenishmentRequests = ({ warehouses, statuses, hasPurchaseRequests }): JSX.Element => {
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { isSubmitting, isValidating }
  } = useForm({
    mode: "onChange"
  });
  const [isLoading, setIsLoading] = React.useState(false);
  const [purchaseRequests, setPurchaseRequests] = React.useState([]);
  const [purchaseRequest, setPurchaseRequest] = React.useState(null);
  const [showPurchaseRequestModal, setShowPurchaseRequestModal] = React.useState(false);
  const [showApprovePurchaseRequestModal, setShowApprovePurchaseRequestModal] = React.useState(false);
  const [showCancelPurchaseRequestModal, setShowCancelPurchaseRequestModal] = React.useState(false);
  const [warehouseFilter, setWarehouseFilter] = React.useState("");
  const [itemsForDestruction, setItemsForDestruction] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState("");
  const [pagination, setPagination] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(1);
  const table = React.useRef(null);
  const abortController = React.useRef<AbortController>();
  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: "code",
        Header: "Purchase Request",
        accessor: "code",
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Created",
        accessor: "created_at",
        Cell: (props) => {
          const createdAt = moment(props.value).format("MM/DD/YYYY");

          return (
            <div title={createdAt} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{createdAt}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Closed",
        accessor: "date_closed",
        Cell: (props) => {
          const dateClosed = props.value ? moment(props.value).format("MM/DD/YYYY") : "-";

          return (
            <div title={dateClosed} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{dateClosed}</p>
            </div>
          );
        }
      },
      {
        id: "status",
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_request.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          const isDraft = props.row.original.status == PurchaseRequestStatus.Draft;
          const { policy } = props.row.original;

          return (
            <div className="d-flex justify-content-center">
              <a
                className="button button--action button--action-success"
                title="View Purchase Request"
                data-bs-toggle="tooltip"
                onClick={() => {
                  setPurchaseRequest(props.row.original);
                  setShowPurchaseRequestModal(!showPurchaseRequestModal);
                }}
              >
                <SvgIcon name="view" className="h5" />
              </a>
              {isDraft && (
                <>
                  {policy.approve && (
                    <a
                      className="button button--action button--action-approve"
                      title="Approve Purchase Request"
                      data-bs-toggle="tooltip"
                      onClick={() => {
                        setPurchaseRequest(props.row.original);
                        setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
                      }}
                    >
                      <SvgIcon name="check-circle" className="h6" />
                    </a>
                  )}
                  {policy.cancel && (
                    <a
                      className="button button--action button--action-danger"
                      title="Cancel Purchase Request"
                      data-bs-toggle="tooltip"
                      onClick={() => {
                        setPurchaseRequest(props.row.original);
                        setShowCancelPurchaseRequestModal(!showCancelPurchaseRequestModal);
                      }}
                    >
                      <SvgIcon name="cancel" className="h6" />
                    </a>
                  )}
                </>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const getPurchaseRequests = React.useCallback(
    async ({ warehouseId = null, page, per, filters }) => {
      const warehouse_id = warehouseId || warehouseFilter;

      try {
        setIsLoading(true);
        // If there is a pending fetch request with associated AbortController, abort
        if (abortController.current) abortController.current.abort();
        // Assign a new AbortController for the latest fetch to our useRef variable
        abortController.current = new AbortController();
        const { signal } = abortController.current;

        let response = null;
        if (warehouse_id) {
          response = await api.get(
            replenishment_requests_admin_material_reordering_index_path({ warehouse_id, page, per, filters: JSON.stringify(filters) }),
            { signal }
          );
        } else {
          response = await api.get(replenishment_requests_admin_material_reordering_index_path({ page, per, filters: JSON.stringify(filters) }), {
            signal
          });
        }
        setPurchaseRequests(response.data.data);
        setPagination(response.data.pagination);
        setCurrentPage(response.data.pagination?.current || currentPage);
        return { pagination: response.data.pagination, data: response.data.data };
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    [warehouseFilter]
  );

  const updatePurchaseRequests = (purchaseRequests, updatedPurchaseRequest) => {
    const updatedPurchaseRequests = purchaseRequests.map((purchaseRequest) => {
      if (purchaseRequest.id == updatedPurchaseRequest.id) {
        return { ...purchaseRequest, ...updatedPurchaseRequest };
      }

      return purchaseRequest;
    });

    setPurchaseRequests(updatedPurchaseRequests);
  };

  const approvePurchaseRequest = React.useCallback(async (purchaseRequests, purchaseRequestId) => {
    try {
      const response = await api.put(approve_admin_purchase_request_path(purchaseRequestId));
      updatePurchaseRequests(purchaseRequests, response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const cancelPurchaseRequest = React.useCallback(async (purchaseRequests, purchaseRequestId) => {
    try {
      const response = await api.put(cancel_admin_purchase_request_path(purchaseRequestId));
      updatePurchaseRequests(purchaseRequests, response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const withItemsForDestruction = (data) => {
    const { purchase_request_items } = data;

    return { ...data, purchase_request_items: [...purchase_request_items, ...itemsForDestruction] };
  };

  const save = async (data): Promise<unknown> => {
    const warehouse_id = warehouseFilter || table.current.data.map((d) => d.warehouse?.id)?.[0];

    try {
      await api.put(admin_purchase_request_path(data.id), {
        ...withItemsForDestruction(data)
      });
      await getPurchaseRequests({
        warehouseId: warehouse_id,
        page: 1,
        per: pagination?.limit || 10,
        filters: table.current.state.filters
      });
    } catch (error) {
      console.error(error);
    } finally {
      setShowPurchaseRequestModal(!showPurchaseRequestModal);
    }
  };

  const onError = (data) => {
    console.error(data);
  };

  const onChange = useAsyncDebounce((column, value) => {
    table.current.setFilter(column, value);
  }, 400);

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row>
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No replenishment request history.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Purchase Request Number" onChange={(e) => onChange("code", e.target.value)} />
        </Col>
        <Col>
          <Select
            placeholder="Filter by Warehouse"
            isClearable={false}
            options={warehouses}
            isSearchable={false}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isOptionDisabled={(option) => !option.active}
            onChange={(newOption) => {
              setWarehouseFilter(newOption.id);
            }}
            value={warehouses.find((warehouse) => [warehouseFilter, purchaseRequests[0]?.warehouse?.id].includes(warehouse.id)) || ""}
          />
        </Col>
        <Col sm="auto">
          <Select
            placeholder="Filter by Status"
            options={statuses}
            isSearchable={false}
            getOptionLabel={(option) => I18n.t(`activerecord.attributes.purchase_request.statuses.${option.value}`)}
            getOptionValue={(option) => option.value}
            onChange={(newOption) => {
              if (newOption) {
                setStatusFilter(newOption.value);
                table.current.setFilter("status", newOption.value);
              } else {
                setStatusFilter("");
                table.current.setFilter("status", "");
              }
            }}
            value={statuses.find((status) => status.value == statusFilter) || ""}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <ControlledTableContext.Provider value={{ currentPage, setCurrentPage }}>
            <ControlledTable
              columns={columns}
              data={purchaseRequests}
              ref={table}
              isLoading={isLoading}
              fetchData={getPurchaseRequests}
              totalCount={pagination?.total_count}
              totalPageCount={pagination?.total_pages || 0}
              previous={pagination?.previous}
              next={pagination?.next}
            />
          </ControlledTableContext.Provider>
        </Col>
      </Row>
    );
  };

  const content = () => {
    return hasPurchaseRequests ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  return (
    <React.StrictMode>
      <Card.Body>{content()}</Card.Body>
      <Modal dialogClassName="modal-dialog-centered modal-xl" show={showPurchaseRequestModal}>
        <Modal.Header>{`Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>
          <ReplenishmentRequest
            purchaseRequest={purchaseRequest}
            control={control}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            setItemsForDestruction={setItemsForDestruction}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseRequestModal(!showPurchaseRequestModal);
            }}
            icon="times"
            title="Close"
          />
          {purchaseRequest?.policy?.update &&
            [PurchaseRequestStatus.Draft, PurchaseRequestStatus.Open].includes(purchaseRequest?.status) &&
            purchaseRequest?.purchase_request_items?.some((item) => !(item.purchase_orders?.length > 0)) && (
              <Button
                className="button--brand"
                icon="save"
                title="Save"
                disabled={isSubmitting || isValidating}
                onClick={() => {
                  handleSubmit(async (data) => await save(data), onError)();
                }}
              />
            )}
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showApprovePurchaseRequestModal}>
        <Modal.Header>{`Approve Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to approve purchase request: ${purchaseRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, approve purchase request."
            onClick={() => {
              approvePurchaseRequest(purchaseRequests, purchaseRequest?.id).then(() => {
                setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showCancelPurchaseRequestModal}>
        <Modal.Header>{`Cancel Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to cancel purchase request: ${purchaseRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowCancelPurchaseRequestModal(!showCancelPurchaseRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="cancel"
            title="Yes, cancel purchase request."
            onClick={() => {
              cancelPurchaseRequest(purchaseRequests, purchaseRequest?.id).then(() => {
                setShowCancelPurchaseRequestModal(!showCancelPurchaseRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ReplenishmentRequests;
